import { Inject, Injectable, inject } from '@angular/core';
import { ApiService } from './api.service';
import { API } from './constants';
import { PaginationInput, Transaction } from '../store/types';
import { PollingService } from './polling.service';
import { AuthStore } from '../store/auth.store';

@Injectable({ providedIn: 'root' })
export class TransferService extends ApiService {
  @Inject(PollingService) private pollingService: PollingService;
  private authStore = inject(AuthStore);

  // Almacenar idempotency keys usados en memoria (se limpia al recargar la página)
  private usedIdempotencyKeys = new Set<string>();

  getTransactions(pagination: PaginationInput) {
    return this.post(`${API.getTransactions}`, { body: pagination });
  }

  async transfer(
    dto: {
      sourceAccountNumber: string;
      targetAccountNumber: string;
      amount: number;
      description?: string;
      validationCode?: string;
      transactionType: string;
      invoiceUrl?: string;
      invoiceUrls?: string[];
    },
    metadata: Record<string, any> = {},
    idempotencyKey?: string
  ) {
    // Validar si el idempotency key ya fue usado
    if (idempotencyKey && this.usedIdempotencyKeys.has(idempotencyKey)) {
      return Promise.resolve({
        data: null,
        status: 400,
        error: {
          message: 'Esta transferencia ya fue procesada'
        }
      });
    }

    // Hacer la petición
    const result = await this.post(API.transfer, {
      body: { dto, metadata },
      headers: idempotencyKey
        ? {
            'x-idempotency-key': idempotencyKey
          }
        : {}
    });

    // Marcar el key como usado DESPUÉS de enviar la petición exitosamente
    if (idempotencyKey && result.data?.reference) {
      this.usedIdempotencyKeys.add(idempotencyKey);
    }

    // Las transferencias entre cuentas propias no son extracciones.
    if (result.data?.reference && dto.transactionType !== 'OWNER_TRANSFER') {
      this.authStore.markFirstExtractionLocally();
    }

    return result;
  }

  /**
   * Limpia un idempotency key de la memoria
   * Útil cuando una transferencia falla y se quiere permitir un reintento
   */
  clearIdempotencyKey(key: string): void {
    this.usedIdempotencyKeys.delete(key);
  }

  /**
   * Limpia todos los idempotency keys de la memoria
   * Útil al cerrar sesión o limpiar el estado de la aplicación
   */
  clearAllIdempotencyKeys(): void {
    this.usedIdempotencyKeys.clear();
  }

  getTransaction(reference: string, options?: { silentError?: boolean; background?: boolean }) {
    return this.get<Transaction>(`${API.getTransaction}/${reference}`, options);
  }
}
